// =========================================================================
// ENUMS (Matching the backend exactly)
// =========================================================================

export enum CvMessageActor {
  AI = "ai",
  USER = "user",
  SYSTEM = "system",
}

export enum UserInputType {
  TEXT = "text",
  AUDIO = "audio",
  SKIP = "skip",
}

export enum CvEditorSessionStatus {
  // Phase 1
  NOT_STARTED = "not_started",
  AWAITING_CV_ACTION = "awaiting_cv_action",
  CV_PROCESSING = "cv_processing",
  // Phase 2
  AWAITING_BASICS_VALIDATION = "awaiting_basics_validation",
  AWAITING_MANUAL_BASICS = "awaiting_manual_basics",
  AWAITING_POSITIONS_VALIDATION = "awaiting_positions_validation",
  AWAITING_MANUAL_POSITIONS = "awaiting_manual_positions",
  AWAITING_TARGET_ROLE = "awaiting_target_role",
  PROCESSING_POSITIONS = "processing_positions",
  // Phase 3
  INTERVIEW_LOADING_POSITIONS = "interview_loading_positions",
  INTERVIEW_LOADING_ABOUT_ME = "interview_loading_about_me",
  INTERVIEW_POSITIONS_IN_PROGRESS = "interview_positions_in_progress",
  INTERVIEW_ABOUT_ME_IN_PROGRESS = "interview_about_me_in_progress",
  // Phase 4 & 5
  FINAL_GENERATION = "final_generation",
  COMPLETED = "completed",
  FAILED = "failed",
  ARCHIVED = "archived",

  RESTARTING = "restarting",
}

// =========================================================================
// INTERFACES (Matching the backend exactly)
// =========================================================================

export interface CvMessage {
  id: string;
  actor: CvMessageActor;
  timestamp: string; // Dates are serialized as strings over JSON
  content: AiMessageContent | UserMessageContent | SystemMessageContent;
  positionIndex?: number;
}

export interface CvEditorSession {
  id: number;
  status: CvEditorSessionStatus;

  cvS3Key: string | null;
  targetRole: string | null;
  isBasicsParsed: boolean;
  isPositionsParsed: boolean;
  isOtherSectionsParsed: boolean;

  parsedCvData: any;
  finalCvJson: any;

  currentPositionIndex: number | null;
  positionsChatHistory: CvMessage[];
  aboutMeChatHistory: CvMessage[];

  totalAudioDuration: number;
  locale: string;

  createdAt: string;
  lifetimeAudioSecondsRemaining: number;
  sessionAudioSecondsUsed: number;
}

// Type for the lightweight status polling response
export interface SessionStatusResponse {
  id: number;
  status: CvEditorSessionStatus;
  parsedCvData: any;
  isBasicsParsed: boolean;
  isPositionsParsed: boolean;
  finalCvJson?: any | null;
  positionsChatHistory?: any[];
  aboutMeChatHistory?: any[];
  currentPositionIndex?: number | null;
}

// Type for the response from the 'proceedToBasics' endpoint
export interface ProceedToBasicsResponse {
  session: SessionStatusResponse;
  isDataReady: boolean;
  isManualEntry: boolean;
}

/**
 * The content of a message from the AI.
 */

export interface AiMessageContent {
  text: string;
}

/**
 * The content of a message from the User.
 */
export interface UserMessageContent {
  type: UserInputType;
  originalText?: string; // The transcribed text from audio, or the typed text
  s3AudioKey?: string;
  audioOriginalName?: string;
  audioDuration?: number; // Duration in seconds
  isSkip: boolean;
}

/**
 * The content of a message generated by the System.
 */
export interface SystemMessageContent {
  text: string;
}
